import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, apiRequest } from '../lib/api';
import { usePortal } from '../lib/portal';
import { Alert, Button, Field, TextInput } from '../components/ui';
import '../styles/auth.css';

import { EnvBadge } from '../components/EnvBadge';
export function ForgotPassword() {
  const { siteHome } = usePortal();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * TWO DIFFERENT SILENCES, AND ONLY ONE OF THEM IS DELIBERATE.
   *
   * An address that is not registered must still be answered "if an account
   * exists, we've sent a link" — otherwise this form tells a stranger which of
   * your customers bank here. The server returns 201 for both cases precisely
   * so this page cannot tell them apart, and that stays.
   *
   * What used to be here was `catch { }` around the whole call, which swallowed
   * the OTHER silence too: a 400 from an empty field, a 429 from retrying, a
   * 500 from a server in trouble — all reported to the user as "we've sent a
   * reset link". That is the one failure somebody could have acted on, and the
   * page hid it. A real customer spent nine minutes on this screen being told
   * her mail was on its way while one request had in fact been rejected.
   *
   * So: a request that SUCCEEDED says nothing revealing, and a request that
   * FAILED says so.
   */
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await apiRequest('/api/v1/auth/password-reset/request',
        { method: 'POST', auth: false, body: { email: email.trim() } });
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? 'Enter the email address you sign in with.'
        : err instanceof ApiError && err.status === 429
          ? 'That is several requests in a row. Wait a minute and try once more — '
            + 'and check your spam folder, the first mail may already be there.'
        : 'We could not send that just now. Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-brand-row">
        <Link to={siteHome} className="auth-brand">NyumbaSuite</Link>
        <EnvBadge />
      </div>
      <form className="auth-card login" onSubmit={submit} noValidate>
        <h1 className="auth-title">Reset your password</h1>
        {sent ? (
          <>
            <Alert tone="success">If an account exists for that address, we’ve sent a reset link. Check your email.</Alert>
            <div className="auth-foot"><Link to="/login">Back to sign in</Link></div>
          </>
        ) : (
          <>
            <p className="auth-lede">Enter your email and we’ll send a reset link.</p>
            {error && <div style={{ marginBottom: 12 }}><Alert tone="danger">{error}</Alert></div>}
            <Field label="Email" htmlFor="email">
              <TextInput id="email" type="email" inputMode="email" autoComplete="email" autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)} required />
            </Field>
            <Button type="submit" block loading={busy}>Send reset link</Button>
            <div className="auth-foot">
              <Link to="/login">Back to sign in</Link>
              <span> · </span>
              <Link to="/register">Create an account</Link>
            </div>
          </>
        )}
      </form>
      <Link className="auth-link" to={siteHome}>← Back to homepage</Link>
    </div>
  );
}
